import React, { useCallback, useEffect, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Schema } from '@fhs/client';
import { MenuItemDetails } from '@fhs/menu/src/components/menu-item-details';
import { MenuItemSkeletonLoading } from '@fhs/menu/src/components/menu-item-skeleton';
import { ComboSelectionProvider } from '@fhs/menu/src/state/combo-selection';
import { MenuPickerProvider, useMenuPicker } from '@fhs/menu/src/state/menu-picker';
import {
  EditCartEntryResult,
  useEditCartEntry,
} from '@fhs/menu/src/utils/use-edit-from-cart-state';
import { useTransformForAddToCart } from '@fhs/menu/src/utils/use-transform-for-add-to-cart';
import {
  ActionSheet,
  Button,
  ErrorCard,
  MediaQueriesContext,
  mobileMediaQueries,
  tokens,
} from '@fhs/ui';
import { ICartEntry } from '@rbi-ctg/menu';

import { ProductWizard } from '../../components';
import { useLegacyMenuStates } from '../../hooks/use-legacy-menu-states';
import { useOfferCart, useSelectedStep } from '../../state/offer-guide-cart-state';

import { BottomButtonContainer } from './bottom-button';
import { FadeView } from './content-renderer';

function EligibleItemRendererV2({
  addItemToSimplyOfferCart,
  editCartEntryQuery,
  setOnBackAttempt,
}: {
  addItemToSimplyOfferCart: (cartEntry: ICartEntry) => void;
  editCartEntryQuery: EditCartEntryResult['query'];
  setOnBackAttempt: (callback: (() => boolean) | undefined) => void;
}) {
  const [isShowNutrition, setIsShowNutrition] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const menuPicker = useMenuPicker();
  const { shouldShowCustomizationScreen, setShouldShowCustomizationScreen } = menuPicker;

  const transformForAddToCart = useTransformForAddToCart();

  const handleAddToCart = useCallback(async () => {
    try {
      setIsLoading(true);
      const itemId = menuPicker.selectedPickerAspectOption?.itemId;
      if (itemId) {
        const cartEntry = await transformForAddToCart({
          itemId,
          quantity: 1,
          isCombo: false,
          pickerSelections: menuPicker.pickerSelections,
          resolvedCustomizationSelections: menuPicker.resolvedCustomizationSelections,
          selectedPickerAspectOption: menuPicker.selectedPickerAspectOption,
        });

        cartEntry && addItemToSimplyOfferCart(cartEntry);
      }
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  }, [
    transformForAddToCart,
    addItemToSimplyOfferCart,
    menuPicker.pickerSelections,
    menuPicker.resolvedCustomizationSelections,
    menuPicker.selectedPickerAspectOption,
  ]);

  useEffect(() => {
    if (shouldShowCustomizationScreen) {
      setOnBackAttempt(() => {
        setShouldShowCustomizationScreen(false);
        return true;
      });
    } else {
      setOnBackAttempt(undefined);
    }

    return () => setOnBackAttempt(undefined);
  }, [setOnBackAttempt, shouldShowCustomizationScreen, setShouldShowCustomizationScreen]);

  if (menuPicker.query.isLoading || editCartEntryQuery.isLoading) {
    return (
      <MediaQueriesContext.Provider value={mobileMediaQueries}>
        <View style={styles.loading}>
          <MenuItemSkeletonLoading hideFooter />
        </View>
      </MediaQueriesContext.Provider>
    );
  }

  if (menuPicker.query.isError || editCartEntryQuery.isError || !menuPicker.query.data) {
    return <ErrorCard description="Product not found" hideButton />;
  }

  return (
    <>
      <FadeView style={{ flex: 1 }}>
        <MediaQueriesContext.Provider value={mobileMediaQueries}>
          <MenuItemDetails
            renderScrollComponent={ActionSheet.ScrollView}
            isShowingNutrition={isShowNutrition}
            setIsShowingNutrition={setIsShowNutrition}
          />
        </MediaQueriesContext.Provider>
      </FadeView>
      {!shouldShowCustomizationScreen && (
        <BottomButtonContainer>
          <Button size="xl" onPress={handleAddToCart} loading={isLoading}>
            <Button.Text>Add Item</Button.Text>
          </Button>
        </BottomButtonContainer>
      )}
    </>
  );
}

function EligibleItemRenderer({
  productId,
  addItemToSimplyOfferCart,
}: {
  productId: string;
  addItemToSimplyOfferCart: (cartEntry: ICartEntry) => void;
}) {
  const { product, loading } = useLegacyMenuStates({
    productId,
  });
  const { bottom } = useSafeAreaInsets();

  if (loading) {
    return (
      <MediaQueriesContext.Provider value={mobileMediaQueries}>
        <View style={styles.loading}>
          <MenuItemSkeletonLoading hideFooter />
        </View>
      </MediaQueriesContext.Provider>
    );
  }

  if (product) {
    return (
      <FadeView
        key={product?._id}
        style={{
          flex: 1,
          paddingBottom: bottom,
        }}
      >
        <ProductWizard
          product={product}
          isSimplyOffer
          addItemToSimplyOfferCart={addItemToSimplyOfferCart}
        />
      </FadeView>
    );
  }

  return <ErrorCard description="Product not found" hideButton />;
}
interface Props {
  selectedEligibleItem: Schema['MenuSectionItem']['type'];
}

export function SelectedEligibleItem({ selectedEligibleItem }: Props) {
  const {
    currentStepIndex,
    modifyItemSelectionInStep,
    isEdit,
    setOnBackAttempt,
    enableSimplifiedMenuDetailView,
  } = useOfferCart();

  const selectedStep = useSelectedStep();

  const editCartEntry = useEditCartEntry(
    selectedStep?.selectedEntry,
    isEdit && enableSimplifiedMenuDetailView
  );
  if (enableSimplifiedMenuDetailView) {
    return (
      <MenuPickerProvider
        keepCustomizationInView
        slug={selectedEligibleItem.slug}
        isInitialStateLoading={editCartEntry.query.isLoading}
        initialState={
          isEdit
            ? {
                selectedPickerAspectOptionId:
                  editCartEntry.query.data?.selectedPickerAspectOptionId ?? undefined,
                userCustomizationSelections: editCartEntry.query.data?.userCustomizationSelections,
                instructions: editCartEntry.query.data?.instructions,
              }
            : undefined
        }
      >
        <ComboSelectionProvider
          initialState={
            isEdit
              ? {
                  isComboEnabled: editCartEntry.query.data?.isComboEnabled,
                  comboSelectedPickers: undefined,
                }
              : undefined
          }
        >
          <EligibleItemRendererV2
            editCartEntryQuery={editCartEntry.query}
            addItemToSimplyOfferCart={cartEntry => {
              modifyItemSelectionInStep(currentStepIndex, cartEntry);
            }}
            setOnBackAttempt={setOnBackAttempt}
          />
        </ComboSelectionProvider>
      </MenuPickerProvider>
    );
  }

  return (
    <EligibleItemRenderer
      productId={selectedEligibleItem._legacySlug}
      addItemToSimplyOfferCart={cartEntry => {
        modifyItemSelectionInStep(currentStepIndex, cartEntry);
      }}
    />
  );
}

const styles = StyleSheet.create({
  loading: {
    flex: 1,
    backgroundColor: tokens.colors.$white,
  },
});
